import React, { useCallback, useEffect, useState } from 'react';
import {
  createLead,
  downloadCsv,
  leadsCsvPath,
  listLeads,
} from '../../../api/adminCrm';
import { LEAD_STATUSES, formatWhen, labelFor } from './format';
import './Crm.css';

const EMPTY_FORM = {
  email: '',
  name: '',
  source: '',
  status: 'waitlist',
  notes: '',
};

function LeadsPanel() {
  const [draftQuery, setDraftQuery] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ leads: [], total: 0, page: 1, page_size: 50 });
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listLeads({ q: query, status, page });
      setResult({
        leads: data?.leads || [],
        total: data?.total || 0,
        page: data?.page || page,
        page_size: data?.page_size || 50,
      });
    } catch (err) {
      setError(err.message || 'The lead list did not load.');
    } finally {
      setLoading(false);
    }
  }, [query, status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const pageCount = Math.max(1, Math.ceil(result.total / result.page_size));

  const onSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  };

  const onCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await createLead({
        email: form.email,
        name: form.name,
        source: form.source,
        status: form.status,
        notes: form.notes,
      });
      setForm(EMPTY_FORM);
      setPage(1);
      if (page === 1 && query === '' && status === '') {
        await load();
      } else {
        setQuery('');
        setStatus('');
        setDraftQuery('');
      }
    } catch (err) {
      setError(err.message || 'That lead was not added.');
    } finally {
      setSaving(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    setError('');
    try {
      await downloadCsv(leadsCsvPath({ q: query, status }), 'leads.csv');
    } catch (err) {
      setError(err.message || 'The CSV did not download.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="crm-panel" aria-labelledby="leads-heading">
      <div className="crm-panel__heading">
        <div>
          <p className="crm-eyebrow">Waiting</p>
          <h2 id="leads-heading">Leads</h2>
          <p className="crm-panel__intro">
            People who have not signed up yet. Add someone by hand, or export the list.
          </p>
        </div>
        <button type="button" className="crm-button" onClick={onExport} disabled={exporting}>
          {exporting ? 'Preparing CSV…' : 'Export CSV'}
        </button>
      </div>

      <form className="crm-form" onSubmit={onCreate}>
        <div className="crm-field">
          <label htmlFor="lead-email">Email</label>
          <input
            id="lead-email"
            type="email"
            required
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            autoComplete="off"
          />
        </div>
        <div className="crm-field">
          <label htmlFor="lead-name">Name</label>
          <input
            id="lead-name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            autoComplete="off"
          />
        </div>
        <div className="crm-field">
          <label htmlFor="lead-source">Source</label>
          <input
            id="lead-source"
            value={form.source}
            onChange={(event) => setForm({ ...form, source: event.target.value })}
            autoComplete="off"
          />
        </div>
        <div className="crm-field">
          <label htmlFor="lead-status">Status</label>
          <select
            id="lead-status"
            value={form.status}
            onChange={(event) => setForm({ ...form, status: event.target.value })}
          >
            {LEAD_STATUSES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="crm-field crm-field--grow">
          <label htmlFor="lead-notes">Notes</label>
          <input
            id="lead-notes"
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />
        </div>
        <button type="submit" className="crm-button crm-button--primary" disabled={saving}>
          {saving ? 'Adding…' : 'Add lead'}
        </button>
      </form>

      <form className="crm-toolbar" onSubmit={onSearch}>
        <div className="crm-field crm-field--grow">
          <label htmlFor="lead-search">Search name or email</label>
          <input
            id="lead-search"
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="crm-field">
          <label htmlFor="lead-filter-status">Status</label>
          <select
            id="lead-filter-status"
            value={status}
            onChange={(event) => {
              setPage(1);
              setStatus(event.target.value);
            }}
          >
            <option value="">All</option>
            {LEAD_STATUSES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <button type="submit" className="crm-button">Search</button>
      </form>

      {error ? <p className="crm-error" role="alert">{error}</p> : null}
      {loading ? <p className="crm-status">Loading leads…</p> : null}
      {!loading && result.leads.length === 0 ? (
        <p className="crm-status">No leads match this search.</p>
      ) : null}

      {result.leads.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Name</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
                <th scope="col">Notes</th>
                <th scope="col">Added</th>
              </tr>
            </thead>
            <tbody>
              {result.leads.map((lead) => (
                <tr key={lead.id}>
                  <td>{lead.email}</td>
                  <td>{lead.name || '—'}</td>
                  <td>{lead.source || '—'}</td>
                  <td>{labelFor(LEAD_STATUSES, lead.status)}</td>
                  <td>{lead.notes || '—'}</td>
                  <td>{formatWhen(lead.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="crm-pager">
        <button
          type="button"
          className="crm-button"
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          disabled={page <= 1 || loading}
        >
          Previous page
        </button>
        <p className="crm-status">Page {page} of {pageCount}</p>
        <button
          type="button"
          className="crm-button"
          onClick={() => setPage((current) => current + 1)}
          disabled={page >= pageCount || loading}
        >
          Next page
        </button>
      </div>
    </section>
  );
}

export default LeadsPanel;
